import { RefObject, useCallback, useEffect, useState } from 'react';

// Keep a module-level reference of the most recent element that entered PiP
let lastPiPVideoEl: any | null = null;

export const exitAnyPictureInPicture = async () => {
  const docAny = document as any;
  // Standard API can exit globally without a specific element
  try {
    if (docAny && 'exitPictureInPicture' in docAny && docAny.pictureInPictureElement) {
      await docAny.exitPictureInPicture();
    }
  } catch {}

  // Safari/WebKit requires operating on the original element
  try {
    if (
      lastPiPVideoEl &&
      typeof lastPiPVideoEl.webkitPresentationMode === 'string' &&
      typeof lastPiPVideoEl.webkitSetPresentationMode === 'function' &&
      lastPiPVideoEl.webkitPresentationMode === 'picture-in-picture'
    ) {
      lastPiPVideoEl.webkitSetPresentationMode('inline');
    }
  } catch {}
};

const usePictureInPicture = (videoRef: RefObject<HTMLVideoElement | null>) => {
  const [isPiP, setIsPiP] = useState(false);

  const exitPictureInPicture = useCallback(async () => {
    const videoEl = videoRef.current as any;
    if (!videoEl) return;

    // Standard PiP
    try {
      const docAny = document as any;
      if (
        docAny &&
        'pictureInPictureElement' in docAny &&
        docAny.pictureInPictureElement === videoEl &&
        typeof docAny.exitPictureInPicture === 'function'
      ) {
        await docAny.exitPictureInPicture();
        setIsPiP(false);
        return;
      }
    } catch {}

    // Safari/WebKit PiP
    try {
      if (
        typeof videoEl.webkitPresentationMode === 'string' &&
        typeof videoEl.webkitSetPresentationMode === 'function' &&
        videoEl.webkitPresentationMode === 'picture-in-picture'
      ) {
        videoEl.webkitSetPresentationMode('inline');
        setIsPiP(false);
      }
    } catch {}
  }, [videoRef]);

  // Detect Picture-in-Picture state for both standard and Safari/WebKit
  useEffect(() => {
    const videoEl = videoRef.current as any;
    if (!videoEl) return;

    const handleEnter = () => {
      lastPiPVideoEl = videoEl;
      setIsPiP(true);
    };
    const handleLeave = () => setIsPiP(false);

    try {
      videoEl.addEventListener?.('enterpictureinpicture', handleEnter);
      videoEl.addEventListener?.('leavepictureinpicture', handleLeave);
    } catch {}

    // Safari/webkit specific presentation mode change
    const handleWebkitChange = () => {
      try {
        const inPiP = videoEl.webkitPresentationMode === 'picture-in-picture';
        if (inPiP) lastPiPVideoEl = videoEl;
        setIsPiP(inPiP);
      } catch {}
    };
    try {
      videoEl.addEventListener?.('webkitpresentationmodechanged', handleWebkitChange);
    } catch {}

    // Initialize current state (in case we mount while already in PiP)
    handleWebkitChange();

    return () => {
      try {
        videoEl.removeEventListener?.('enterpictureinpicture', handleEnter);
        videoEl.removeEventListener?.('leavepictureinpicture', handleLeave);
        videoEl.removeEventListener?.('webkitpresentationmodechanged', handleWebkitChange);
      } catch {}
    };
  }, [videoRef]);

  return { isPiP, setIsPiP, exitPictureInPicture };
};

export default usePictureInPicture;
